'use client'

import { clsx } from 'clsx'
import { describedBy, Field } from '@/ui/Field'
import { CheckIcon } from '@/ui/icons/CheckIcon'

type Option<T extends string> = {
  value: T
  label: string
  disabled?: boolean
}

type Props<T extends string> = {
  /** Groups the radios and prefixes each option's id. */
  name: string
  /** Names the group. A `<label for>` cannot name a set of radios, so the control renders its own. */
  label: string
  options: readonly Option<T>[]
  value: T
  onChange: (value: T) => void
  error?: string
  required?: boolean
  disabled?: boolean
  className?: string
}

/** Pill track with one selected segment; a native radio group underneath, so arrow keys work. */
export function SegmentedControl<T extends string>({
  name,
  label,
  options,
  value,
  onChange,
  error,
  required,
  disabled,
  className,
}: Props<T>) {
  const labelId = `${name}-label`

  return (
    <Field fieldId={name} error={error} className={className}>
      <div className="flex items-baseline">
        <span id={labelId} className="text-label text-neutral-800">
          {label}
        </span>
        {required ? (
          <span aria-hidden="true" className="ms-1 text-error-600">
            *
          </span>
        ) : null}
      </div>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        aria-describedby={describedBy(name, error)}
        aria-invalid={error ? true : undefined}
        aria-required={required ? true : undefined}
        className={clsx(
          // inset-ring instead of border: a border would eat 2px of the 42px track and the
          // 38px pill would overflow it.
          'flex h-10.5 w-fit rounded-full bg-neutral-100 p-0.5 inset-ring-1 inset-ring-neutral-400',
          error && 'inset-ring-error-600',
          disabled && 'opacity-60'
        )}
      >
        {options.map((option) => {
          const selected = option.value === value
          const off = disabled === true || option.disabled === true
          return (
            <label key={option.value} className="flex">
              <input
                id={`${name}-${option.value}`}
                type="radio"
                name={name}
                value={option.value}
                checked={selected}
                disabled={off}
                onChange={() => onChange(option.value)}
                className="peer sr-only"
              />
              <span
                className={clsx(
                  'flex h-9.5 items-center justify-center gap-2 rounded-full px-4 text-field',
                  'peer-focus-visible:focus-ring',
                  // Disabling dims the selection, it does not erase it.
                  selected && 'bg-accent-500 text-segment-on',
                  !selected && (off ? 'text-neutral-600' : 'text-primary-500'),
                  off ? 'cursor-not-allowed' : 'cursor-pointer'
                )}
              >
                {selected ? <CheckIcon className="w-4 shrink-0" /> : null}
                <span className="cap-band">{option.label}</span>
              </span>
            </label>
          )
        })}
      </div>
    </Field>
  )
}
